import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTitle } from "../../context/TitleContext";
import { useDispatch, useSelector } from "react-redux";
import { fetchFAQs, deleteFAQ, clearFAQState } from "../../redux/Slices/FAQSlice";
import { toast } from "react-toastify";
import AddFAQ from "./AddFAQ";
import EditFAQ from "./EditFAQ";
import PermissionButton from "../PermissionButton";
import { FaEdit, FaTrash } from "react-icons/fa";
import Pagination from '../../components/Pagination';

const FAQs = () => {
  const { t, i18n } = useTranslation("global");
  const { setTitle } = useTitle();
  const dispatch = useDispatch();

  const { data: faqs, meta, loading, error, deleteLoading, deleteSuccessMessage, deleteError, addSuccessMessage } = useSelector((state) => state.faqs);

  const [showAddForm, setShowAddForm]       = useState(false);
  const [editingFAQ, setEditingFAQ]         = useState(null);
  const [currentPage, setCurrentPage]       = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingFAQ, setDeletingFAQ]       = useState(null);

  useEffect(() => {
    setTitle(t("sidenav.faqs"));
  }, [setTitle, t]);

  useEffect(() => {
    dispatch(fetchFAQs(currentPage));
  }, [dispatch, currentPage]);

  const handleDelete = (faq) => {
    setDeletingFAQ(faq);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (deletingFAQ) {
      dispatch(deleteFAQ(deletingFAQ.id));
    }
  };

  const handleEdit = (faq) => {
    setEditingFAQ(faq);
    setShowAddForm(false);
  };

  const handleCancelEdit = () => {
    setEditingFAQ(null);
  };

  const handlePageChange = (page) => {
    if (loading) return;
    if (page === currentPage) return;
    setCurrentPage(page);
  };



  useEffect(() => { if (error) toast.error(error); }, [error]);

  useEffect(() => {
    if (deleteSuccessMessage) {
      setShowDeleteModal(false);
      setDeletingFAQ(null);
      setTimeout(() => { toast.success(deleteSuccessMessage, { position: "top-center", zIndex: 9999 }); }, 100);
      dispatch(fetchFAQs(currentPage));
      dispatch(clearFAQState());
    }
    if (deleteError) {
      toast.error(deleteError, { position: "top-center", zIndex: 9999 });
      dispatch(clearFAQState());
    }
  }, [deleteSuccessMessage, deleteError, dispatch, currentPage]);

  useEffect(() => {
    if (addSuccessMessage) {
      dispatch(fetchFAQs(currentPage));
    }
  }, [addSuccessMessage, dispatch, currentPage]);

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString('ar-EG', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const overlay = { position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", zIndex: 1040, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 };

  return (
    <div style={{ padding: "4px 0", direction: i18n.language === 'ar' ? 'rtl' : 'ltr' }}>

      {/* ── Header ─────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <h4 style={{ margin: 0, fontWeight: 800, color: "#0f172a", fontSize: 20, display: "flex", alignItems: "center", gap: 8 }}>
            ❓ {t("sidenav.faqs")}
          </h4>
          <p style={{ margin: "2px 0 0", color: "#64748b", fontSize: 13 }}>تعديل وإضافة الأسئلة الشائعة التي تظهر للعملاء</p>
        </div>
        <PermissionButton
          permission="create-faqs"
          onClick={() => {
            setShowAddForm(!showAddForm);
            setEditingFAQ(null);
          }}
          style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 20px", borderRadius: 10, border: "none",
            background: showAddForm ? "#f1f5f9" : "linear-gradient(135deg,var(--main-color),var(--main-light))", color: showAddForm ? "#475569" : "#fff", fontWeight: 700, cursor: "pointer", fontSize: 14 }}
        >
          {showAddForm ? "✕ إخفاء النموذج" : `＋ ${t("btns.addNewQuestion")}`}
        </PermissionButton>
      </div>

      {/* ── Add Form ─────────────────────────────────── */}
      {showAddForm && (
        <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", padding: "20px 22px", marginBottom: 20, boxShadow: "0 2px 12px rgba(99,102,241,.08)" }}>
          <div style={{ fontWeight: 700, color: "var(--main-color)", fontSize: 15, marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
            ＋ إضافة سؤال جديد
          </div>
          <AddFAQ onSuccess={() => setShowAddForm(false)} />
        </div>
      )}

      {/* ── Edit Form ─────────────────────────────────── */}
      {editingFAQ && (
        <div style={{ background: "#fff", borderRadius: 14, border: "1px solid var(--warning-color)", padding: "20px 22px", marginBottom: 20, boxShadow: "0 2px 12px rgba(251,191,36,.12)" }}>
          <div style={{ fontWeight: 700, color: "var(--warning-color)", fontSize: 15, marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
            <FaEdit /> تعديل السؤال
          </div>
          <EditFAQ faq={editingFAQ} onSuccess={handleCancelEdit} />
        </div>
      )}

      {/* ── Loading ───────────────────────────────────── */}
      {loading && !faqs?.length && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {[...Array(4)].map((_, i) => (
            <div key={i} style={{ height: 100, borderRadius: 12, background: "linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite" }} />
          ))}
          <style>{`@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
        </div>
      )}

      {/* ── Empty ─────────────────────────────────────── */}
      {!loading && faqs?.length === 0 && (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
          <div style={{ fontSize: 52 }}>❓</div>
          <p style={{ marginTop: 12, fontWeight: 600, fontSize: 15, color: "#64748b" }}>{t("labels.noData")}</p>
        </div>
      )}

      {/* ── Accordion/Card List ───────────────────────── */}
      {!loading && faqs?.length > 0 && (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {faqs.map((faq, idx) => (
              <div key={faq.id}
                style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", padding: "16px 20px", display: "flex", flexDirection: "column", gap: 10, boxShadow: "0 1px 4px rgba(0,0,0,.04)", transition: "box-shadow .2s" }}
                onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 4px 16px rgba(99,102,241,.1)")}
                onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,.04)")}
              >
                {/* Top Row: Q + status + actions */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 14 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 5 }}>
                      <span style={{ fontSize: 11, color: "#94a3b8" }}>#{meta ? (meta.current_page - 1) * (meta.per_page || 10) + idx + 1 : idx + 1}</span>
                      {(faq.status === 1 || faq.status === '1' || faq.status === 'active') ? (
                        <span style={{ padding: "2px 10px", borderRadius: 20, background: "#d1fae5", color: "#065f46", fontSize: 11, fontWeight: 700 }}>{t("labels.active")}</span>
                      ) : (
                        <span style={{ padding: "2px 10px", borderRadius: 20, background: "#f1f5f9", color: "#64748b", fontSize: 11, fontWeight: 700 }}>{t("labels.inactive")}</span>
                      )}
                      <span style={{ fontSize: 11, color: "#cbd5e1" }}>|</span>
                      <span style={{ fontSize: 11, color: "#94a3b8" }}>📅 {formatDate(faq.created_at)}</span>
                    </div>

                    <h5 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#0f172a", lineHeight: 1.4 }}>
                      {faq.question_ar}
                    </h5>
                    {faq.question_en && (
                      // BUG-59 fix: فرض اتجاه LTR صريح (بالسمة dir وليس فقط CSS) حتى لا تظهر علامة الاستفهام في بداية النص الإنجليزي
                      <p dir="ltr" style={{ margin: "4px 0 0", fontSize: 13, color: "#94a3b8", direction: "ltr", textAlign: "left", unicodeBidi: "plaintext" }}>
                        {faq.question_en}
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                    <PermissionButton permission="edit-faqs" onClick={() => handleEdit(faq)} title={t("labels.edit")}
                      style={{ width: 34, height: 34, borderRadius: 8, border: "1px solid var(--warning-color)", background: "#fefce8", color: "var(--warning-color)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>
                      <FaEdit size={14} />
                    </PermissionButton>
                    <PermissionButton permission="delete-faqs" onClick={() => handleDelete(faq)} disabled={deleteLoading} title={t("labels.delete")}
                      style={{ width: 34, height: 34, borderRadius: 8, border: "1px solid #fecaca", background: "#fef2f2", color: "var(--red-color)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>
                      <FaTrash size={14} />
                    </PermissionButton>
                  </div>
                </div>

                {/* Bottom Row: Answer box */}
                <div style={{ background: "#f8fafc", borderRadius: 10, padding: "12px 16px", borderRight: "3px solid #cbd5e1" }}>
                  <div style={{ fontSize: 13, color: "#475569", lineHeight: 1.6, whiteSpace: "pre-line" }}>
                    {faq.answer_ar}
                  </div>
                  {faq.answer_en && (
                    // BUG-59 fix: نفس إصلاح اتجاه LTR لنص الإجابة الإنجليزية
                    <div dir="ltr" style={{ fontSize: 12, color: "#64748b", direction: "ltr", textAlign: "left", unicodeBidi: "plaintext", borderTop: "1px dashed #e2e8f0", marginTop: 8, paddingTop: 8, whiteSpace: "pre-line" }}>
                      {faq.answer_en}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <Pagination meta={meta} currentPage={currentPage} loading={loading} onChange={handlePageChange} />
        </>
      )}

      {/* ── Delete Modal ──────────────────────────────── */}
      {showDeleteModal && (
        <div style={overlay}>
          <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 480, boxShadow: "0 25px 60px rgba(0,0,0,.25)", overflow: "hidden" }}>
            <div style={{ padding: "18px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 700, fontSize: 17, display: "flex", alignItems: "center", gap: 6 }}><FaTrash /> تأكيد الحذف</span>
              <button onClick={() => { setShowDeleteModal(false); setDeletingFAQ(null); }} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94a3b8" }}>✕</button>
            </div>
            <div style={{ padding: "18px 24px" }}>
              <p style={{ margin: 0, color: "#334155" }}>هل أنت متأكد من حذف هذا السؤال؟</p>
              <div style={{ marginTop: 10, padding: "10px 14px", background: "#f8fafc", borderRadius: 8, fontSize: 13, color: "#64748b", borderRight: "3px solid var(--red-color)" }}>
                {deletingFAQ?.question_ar}
              </div>
              <p style={{ margin: "10px 0 0", color: "var(--red-color)", fontSize: 13 }}>⚠ هذا الإجراء لا يمكن التراجع عنه.</p>
            </div>
            <div style={{ padding: "14px 24px", background: "#f8fafc", display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => { setShowDeleteModal(false); setDeletingFAQ(null); }} style={{ padding: "8px 20px", borderRadius: 9, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer" }}>إلغاء</button>
              <button onClick={confirmDelete} disabled={deleteLoading} style={{ padding: "8px 20px", borderRadius: 9, border: "none", background: "var(--red-color)", color: "#fff", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                {deleteLoading ? <><span className="spinner-border spinner-border-sm"></span> جاري الحذف...</> : "حذف السؤال"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FAQs;